import cx from "classnames";
import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  IOption,
  PageHeader,
  Search,
  Head,
  PerformerAppPath,
  PerformerAppSmartBanner,
  RouterProps,
} from "component-library";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { dataTestIds } from "data-testids";
import { facetGroupCode } from "shared/config/common";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import { ALL_OPPORTUNITIES_FULL_PATH, PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams } from "models/api";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IJobSender,
  OpenClosedRoles,
  ShowType,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";
import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";

import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import { IJobsPageInjectedProps } from "pages/JobsPage/JobsPage";
import {
  getOpenClosedRolesFilter,
  INPUT_NAMES,
} from "pages/JobsPage/JobsPage.helpers";
import { checkIsPerformer } from "shared-auth";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import {
  DEFAULT_JOBS_FEEED,
  ALL_OPPORTUNITIES_SEARCH,
  persistSearchAndResetJobsFeedStorage,
  searchRefClear,
  initSearch,
  getInitialValue,
} from "shared/helpers/common";

export interface IAllOpportunitiesPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IAllOpportunitiesPageStoreProps {
  texts: IAllOpportunitiesPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
  jobsFeed: IJobsFeedState;
}

export interface IAllOpportunitiesPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setJobsFeedData: SetJobsFeedData;
}

export interface IAllOpportunitiesPageProps
  extends
    IAllOpportunitiesPageStoreProps,
    IJobsPageInjectedProps,
    RouterProps,
    IAllOpportunitiesPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags {}

export interface IAllOpportunitiesPageState {
  selectedItems: JobTableItemUuid[];
}

export class AllOpportunitiesPage extends React.Component<
  IAllOpportunitiesPageProps,
  IAllOpportunitiesPageState
> {
  constructor(props: any) {
    super(props);

    this.setJobsFeedStorage(
      this.props.router.location.pathname,
      DEFAULT_JOBS_FEEED.PAGENUMBER,
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      this.props.jobsFeed.isBackToJobsFeed
    );

    const pageNumber = this.props.jobsFeed.pageNumber;

    if (this.props.jobsFeed.isBackToJobsFeed && pageNumber) {
      const [, setSearchParams] = this.props.router.searchParams;

      setSearchParams({ page: pageNumber });
    }
  }

  public static pageSize: number = 50;
  public readonly state: Readonly<IAllOpportunitiesPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const {
      filterValues,
      paginatedItems,
      isLoading,
      commonTexts,
      texts,
      renderJobsFilterControl,
      isPerformer,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.MyJobs} />
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText="" />
          }
        />
        {renderJobsFilterControl()}
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={AllOpportunitiesPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={ALL_OPPORTUNITIES_FULL_PATH}
              isPerformer={isPerformer}
              jobsFeed={this.props.jobsFeed}
              setJobsFeedData={this.props.setJobsFeedData}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div
          className={cx(
            "g-col-lg-6",
            checkIsPerformer() ? "g-col-md-6" : "g-col-md-3"
          )}
        >
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
            data-testid={
              dataTestIds.nuJobsApp["MySuggestions.MySuggestions.searchInput"]
            }
            initialValue={getInitialValue(filterValues)}
          />
        </div>
        {this.renderFilter()}
        {this.renderSorter()}
        {this.renderOpenClosedRoles()}
      </div>
    );
  };

  private renderFilter() {
    const { commonTexts, filterValues } = this.props;
    const isPerformer = this.isPerformer();

    if (isPerformer) {
      return null;
    }

    return (
      <div className="g-col-md-3 g-col-lg-2 g-col-sm-6">
        <DropdownInput<string>
          id="opportunitiesFilterType"
          name={INPUT_NAMES.showType}
          variant="select"
          options={commonTexts.filterOptions}
          selected={commonTexts.filterOptions.find(
            (option) => option.value === filterValues[FilterType.ShowType]
          )}
          value={filterValues[FilterType.ShowType]}
          optionSelected={this.handleShowTypeChange}
          notTruncateOptionsOnSmall={!isPerformer}
        />
      </div>
    );
  }

  private renderSorter() {
    const { commonTexts, filterValues } = this.props;
    const isPerformer = this.isPerformer();

    return (
      <div
        className={cx(
          isPerformer ? "g-col-lg-3" : "g-col-md-3 g-col-lg-2",
          "g-col-sm-6"
        )}
      >
        <DropdownInput<string>
          id="opportunitiesSortingType"
          name={INPUT_NAMES.sortingType}
          variant="select"
          options={commonTexts.sortingOptions}
          selected={commonTexts.sortingOptions.find(
            (option) => option.value === filterValues[FilterType.SortingType]
          )}
          value={filterValues[FilterType.SortingType]}
          optionSelected={this.handleSortingTypeChange}
          notTruncateOptionsOnSmall={!isPerformer}
        />
      </div>
    );
  }

  private renderOpenClosedRoles() {
    const isPerformer = this.isPerformer();

    return (
      <div
        className={cx(
          isPerformer ? "g-col-lg-3" : "g-col-md-3 g-col-lg-2",
          isPerformer ? "g-col-sm-6" : "g-col-sm-12"
        )}
      >
        {getOpenClosedRolesFilter(
          this.props,
          (option: IOption<OpenClosedRoles>) =>
            this.props.changeFilters({
              [FilterType.OpenClosedRoles]: option.value,
            })
        )}
      </div>
    );
  }

  private readonly setJobsFeedStorage = (
    pathname: string,
    pageNumber: string,
    breakdownId: string,
    isBackToJobsFeed: boolean
  ) => {
    this.props.setJobsFeedData({
      pathname,
      pageNumber,
      breakdownId,
      isBackToJobsFeed,
    });
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    this.setJobsFeedStorage(
      this.props.jobsFeed.pathname,
      String(page),
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );

    setSearchParams({ page: String(page) });
  };

  private handleShowTypeChange = (option: IOption<ShowType>) => {
    this.props.changeFilters({ [FilterType.ShowType]: option.value });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    persistSearchAndResetJobsFeedStorage({
      setJobsFeedStorage: this.setJobsFeedStorage,
      pathName: this.props.router.location.pathname,
      tab: ALL_OPPORTUNITIES_SEARCH,
      search,
    });

    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    searchRefClear(this.searchRef);
    this.handleSearch(undefined);
  };

  private isPerformer() {
    return this.props.userType === AccountType.Performer;
  }

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private handleRefresh = () => {
    this.props.changeFilters({ [FilterType.PageNumber]: 1 });
    this.props.refreshCounters();
  };

  private initSearch() {
    initSearch(
      ALL_OPPORTUNITIES_SEARCH,
      this.props.jobsFeed.pageNumber,
      this.props.changeFilters
    );
  }

  private registerPageView = () => {
    const { pageTitle } = this.props.texts;

    if (this.isPerformer()) {
      return GTMEvents.jobsFeed.pageViews.allOpportunities(pageTitle);
    }

    return GTMEvents.jobsFeed.pageViews.projects(pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IAllOpportunitiesPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        ownProps.userType === AccountType.Performer
          ? PageTitleKey.JobsFeedAllOpportunities
          : PageTitleKey.JobsFeedProjects
      ),
    },
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
    isPerformer: userType === AccountType.Performer,
    jobsFeed: state.jobsFeed,
  };
};

const mapDispatchToProps: MapDispatchToProps<IAllOpportunitiesPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
  setJobsFeedData,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.All,
  filterContractTypes(facetGroups, userType) {
    if (userType === AccountType.Agent) {
      return facetGroups;
    }

    return facetGroups.filter(
      (facetGroup) => facetGroup.code === facetGroupCode.contractType.lowPay
    );
  },
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IAllOpportunitiesPageProps>(withJobsFilterOptions)
)(AllOpportunitiesPage);
